"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  customerCartUpdatedEventName,
  getCustomerCartIndicator,
  type CustomerCartIndicatorInput,
  type CustomerCartIndicator,
} from "../../lib/ui/customerCartIndicator";
import type { CommerceCartView } from "../../lib/commerce/workflowProvider";

export default function CustomerCartLink({ href, label }: { href: string; label: string }) {
  const pathname = usePathname();
  const [indicator, setIndicator] = useState<CustomerCartIndicator | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    async function loadCartIndicator() {
      try {
        const response = await fetch("/api/portal/cart", {
          cache: "no-store",
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || !result.cart) throw new Error("Cart indicator unavailable.");
        setIndicator(getCustomerCartIndicator(result.cart as CommerceCartView));
      } catch (error) {
        if (!(error instanceof Error && error.name === "AbortError")) setIndicator(null);
      }
    }

    const refresh = (event: Event) => {
      if (event instanceof CustomEvent && event.detail) {
        setIndicator(getCustomerCartIndicator(event.detail as CustomerCartIndicatorInput));
        return;
      }
      void loadCartIndicator();
    };
    void loadCartIndicator();
    window.addEventListener(customerCartUpdatedEventName, refresh);
    window.addEventListener("focus", refresh);
    return () => {
      controller.abort();
      window.removeEventListener(customerCartUpdatedEventName, refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [pathname]);

  const hasItems = Boolean(indicator && indicator.itemCount > 0);
  const accessibleLabel = hasItems
    ? `${label}, ${indicator?.itemCount} product${indicator?.itemCount === 1 ? "" : "s"}${indicator?.totalLabel ? `, estimated total ${indicator.totalLabel}` : ""}`
    : label;

  return (
    <span className="relative isolate my-3 mr-10 inline-flex overflow-visible">
      <Link
        href={href}
        aria-label={accessibleLabel}
        className="inline-flex min-h-12 min-w-36 items-center justify-center whitespace-nowrap rounded-lg bg-[var(--vintera-navy)] px-7 py-2.5 text-center text-white transition hover:bg-[var(--vintera-navy-hover)]"
        style={{ minWidth: "9rem" }}
      >
        {label}
      </Link>
      {hasItems ? (
        <span
          aria-hidden="true"
          title={`${indicator?.itemCount} products in cart`}
          className="pointer-events-none absolute z-20 inline-flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-white bg-[var(--vintera-sage-dark)] px-1 text-[11px] font-bold leading-none text-white shadow-md"
          style={{ right: 0, top: 0, transform: "translate(40%, -45%)" }}
        >
          <span className="translate-y-px">
            {indicator && indicator.itemCount > 99 ? "99+" : indicator?.itemCount}
          </span>
        </span>
      ) : null}
      {hasItems && indicator?.totalLabel ? (
        <span
          aria-hidden="true"
          title={`Estimated cart total ${indicator.totalLabel}`}
          className="pointer-events-none absolute z-20 max-w-28 truncate rounded-full border-2 border-[var(--vintera-navy)] bg-white px-2.5 py-1 text-[11px] font-bold leading-none text-[var(--vintera-navy)] shadow-md"
          style={{ right: 0, bottom: 0, transform: "translate(40%, 45%)" }}
        >
          <span className="inline-block translate-y-px">{indicator.totalLabel}</span>
        </span>
      ) : null}
    </span>
  );
}
